import React, { memo, useEffect, useRef, useState } from 'react';
import type { Color } from 'packages/ds/ColorPicker/types';
import { ColorPicker, type ColorPickerTriggerProps } from 'packages/ds/ColorPicker/ColorPicker';
import { ColorPickerTrigger, type ColorPickerTriggerRef } from './ColorPickerTrigger';
import { isGradientColor } from 'packages/pages/editor/RightSideMenu/FormComponents/ColorPicker/utils';

type Props = {
    isMixed?: boolean;
    id: string;
    value: Color;
    label: string;
    onChange: (value: Color) => void;
    testId: string;
    isDisabled?: boolean;
    isGradientDisabled?: boolean;
    isLeftPositionAllowed?: boolean;
};

const ColorInput = memo<Props>(
    ({
        isMixed,
        id,
        value: defaultValue,
        onChange,
        label,
        testId,
        isDisabled,
        isGradientDisabled,
        isLeftPositionAllowed,
    }: Props) => {
        const colorPickerTriggerRef = useRef<ColorPickerTriggerRef | null>(null);
        const [isColorDragging, setColorDragging] = useState(false);
        const [value, setValue] = useState<Color>(() =>
            typeof defaultValue === 'string'
                ? {
                      color: defaultValue,
                      opacity: 100,
                  }
                : defaultValue,
        );

        const onColorChange = (newValue: Color) => {
            colorPickerTriggerRef.current?.setValue(newValue);
            onChange(newValue);
        };

        useEffect(() => {
            // @ts-ignore
            if (value?.type !== defaultValue?.type) {
                setValue(defaultValue);
            } else if (!isColorDragging) {
                setValue(defaultValue);
            }
        }, [defaultValue]);

        return (
            <ColorPicker
                value={value}
                setColorDragging={setColorDragging}
                isColorDragging={isColorDragging}
                onChange={onColorChange}
                isColorTypeChangeAllowed={!isGradientDisabled}
                isLeftPositionAllowed={isLeftPositionAllowed}
                trigger={({ onClick, isOpen, ref, ...triggerProps }: ColorPickerTriggerProps) => (
                    <ColorPickerTrigger
                        ref={colorPickerTriggerRef}
                        onClick={onClick}
                        isColorDragging={isColorDragging}
                        onChange={onColorChange}
                        triggerRef={ref}
                        triggerProps={triggerProps}
                        defaultValue={value}
                        isMixed={isMixed}
                        id={id}
                        label={label}
                        testId={testId}
                        isDisabled={isDisabled}
                    />
                )}
            />
        );
    },
    arePropsEqual,
);

function arePropsEqual(prevProps: Props, nextProps: Props) {
    const prevKeys = Object.keys(prevProps);
    const nextKeys = Object.keys(nextProps);

    if (prevKeys.length !== nextKeys.length) {
        return false;
    }

    for (const key of prevKeys) {
        if (key === 'value' && typeof prevProps[key] !== 'string') {
            if (typeof prevProps[key] !== typeof nextProps[key]) {
                return false;
            }

            if (getColorType(prevProps[key]) !== getColorType(nextProps[key])) {
                return false;
            }

            if (getOpacity(prevProps[key]) !== getOpacity(nextProps[key])) {
                return false;
            }

            // Gradient comparing could be very heavy, so we just rerender it every time
            if (isGradientColor(nextProps[key])) {
                return false;
            } else if (getColorValue(prevProps[key]) !== getColorValue(nextProps[key])) {
                return false;
            }
        } else if (prevProps[key] !== nextProps[key]) {
            return false;
        }
    }

    return true;
}

function getColorType(color: Color) {
    if (typeof color === 'string') {
        return 'solid';
    }

    if ('type' in color) {
        return color.type;
    }

    return 'solid';
}

function getOpacity(color: Color) {
    if (typeof color === 'string') {
        return 100;
    }

    if ('opacity' in color) {
        return color.opacity;
    }

    return 100;
}

// @ts-ignore
function getColorValue(color: Color) {
    // @ts-ignore
    return 'color' in color ? color.color : color;
}

export default ColorInput;
